import Footnote from '@allesauto/core/footnote';
import JsonLd from '@allesauto/core/JsonLd';
import MobileFilter from '@allesauto/core/mobileFilter';
import Pagination from '@allesauto/core/pagination';
import ProductCardList from '@allesauto/core/productCardList';
import { type Option } from '@allesauto/core/searchDropdown';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import {
  QueryFacets,
  QueryModelsByMake,
  QueryVehicles,
  SchemaTypes,
} from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts, FinanceTypeEn, ViewModes } from '@allesauto/utils/consts';
import {
  createLinkForRouting,
  DefaultFilter,
  getHostFromWhitelabelConfig,
  getPCPFilter,
  SaveFilterToRedux,
} from '@allesauto/utils/funcs';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import { Routes_DE as Routes, Routes_EN } from '@allesauto/utils/routes';
import { useQuery } from '@apollo/client';
import isEqual from 'lodash/isEqual';
import Markdown from 'markdown-to-jsx';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Thing } from 'schema-dts';

import DynamicComponent from '@/components/DynamicComponent';
import Footer from '@/components/Footer/Footer';
import FinanceDetailsOverlay from '@/components/forms/FinanceDetailsOverlay/FinanceDetailsOverlay';
import HorizontalToolbar from '@/components/HorizontalToolbar/HorizontalToolbar';
import Sidebar from '@/components/Sidebars/PCP';

import FilterTagsContainer from '@/features/PCP/containers/FilterTagsContainer';
import { usePCPReduxState } from '@/features/PCP/hooks';
import {
  IParsedQuery,
  ModelsQuery,
  PCPPageProps,
  RouteData,
  RouteFilter,
} from '@/features/PCP/types';
import {
  createVehiclesFilterObject,
  createVehiclesPagerObject,
  createVehiclesSortingObject,
  getDefaultPriceRange,
  getPagesCount,
  getParsedUrlQuery,
  getQueryParams,
} from '@/features/PCP/utils';
import { getUrlFilters } from '@/features/PCP/utils/getUrlReduxFilters';
import { getWhiteLabelConfig } from '@/features/whitelabel/util/getWhiteLabelConfig';
import { initializeApollo } from '@/global/apollo/apollo';
import HeaderContainer from '@/global/containers/HeaderContainer';
import SeoContainer from '@/global/containers/SeoContainer';
import { featureflags } from '@/global/featureflags/featureflags';
import PCPLayout from '@/layouts/pcp/PCP';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';
import {
  setAllFiltersAndSetFlag,
  setFacets,
  setItemsPerPage,
  setPageNumber,
  setPreviousRouteFilter,
  setSorting,
  setViewMode,
} from '@/store/redux/actions/pages/pcp';
import { setPersistVehicleState } from '@/store/redux/slices/pages/pdp';
import { getFilterValues } from '@/utils/filters';
import { markdownProps } from '@/utils/markdown';

import contentDayRegistration from '../../../layouts/pcp/content_dayregistration.json';
import contentDefault from '../../../layouts/pcp/content_default.json';
import contentElectric from '../../../layouts/pcp/content_electric.json';
import contentHybrid from '../../../layouts/pcp/content_hybrid.json';
import contentNew from '../../../layouts/pcp/content_new.json';
import contentSmallCar from '../../../layouts/pcp/content_smallcar.json';
import contentSuv from '../../../layouts/pcp/content_suv.json';
import contentUsed from '../../../layouts/pcp/content_used.json';
import contentVan from '../../../layouts/pcp/content_van.json';

// workaround import path due to webpack bug https://github.com/webpack/webpack/issues/13865
const NoProductCardsResults = dynamic(
  () =>
    import(
      '../../../../../../packages/allesauto-core/src/lib/noProductCardsResults'
    )
);
/**
 * CONSTS
 */
const routes: Record<string, RouteData> = {
  [Routes_EN.CARS.ALL]: {
    story: contentDefault.story,
  },
  [Routes_EN.CARS.NEW]: {
    routeFilter: {
      vehicleCondition: {
        condition: ['neu'],
      },
    },
    story: contentNew.story,
  },
  [Routes_EN.CARS.USED]: {
    routeFilter: {
      vehicleCondition: {
        condition: ['gebraucht'],
      },
    },
    story: contentUsed.story,
  },
  [Routes_EN.CARS.DAY_REGISTRATION]: {
    routeFilter: {
      vehicleCondition: {
        condition: ['tageszulassung'],
      },
    },
    story: contentDayRegistration.story,
  },
  [Routes_EN.CARS.HYBRID]: {
    routeFilter: {
      fuelType: {
        fuelType: ['Hybrid'],
      },
    },
    story: contentHybrid.story,
  },
  [Routes_EN.CARS.ELECTRIC]: {
    routeFilter: {
      fuelType: {
        fuelType: ['Elektro'],
      },
    },
    story: contentElectric.story,
  },
  [Routes_EN.CARS.SMALLCAR]: {
    routeFilter: {
      vehicleType: {
        vehicleType: ['SmallCar'],
      },
    },
    story: contentSmallCar.story,
  },
  [Routes_EN.CARS.VAN]: {
    routeFilter: {
      vehicleType: {
        vehicleType: ['Van'],
      },
    },
    story: contentVan.story,
  },
  [Routes_EN.CARS.SUV]: {
    routeFilter: {
      vehicleType: {
        vehicleType: ['OffRoad'],
      },
    },
    story: contentSuv.story,
  },
};

interface PCPSeoProps {
  story: any;
  whiteLabel: any;
}

// SeoContent - Exclude WhiteLabel
const SeoContent: FC<React.PropsWithChildren<PCPSeoProps>> = memo(
  ({ story, whiteLabel }) =>
    whiteLabel
      ? null
      : story?.content?.body?.map((nestedBlok: any) => {
          // text-component already includes a grid
          if (nestedBlok.component === 'text-component') {
            return (
              <div key={nestedBlok._uid} className="mb-100px">
                <DynamicComponent {...nestedBlok} />
              </div>
            );
          }

          return (
            <div key={nestedBlok._uid} className="grid grid-cols-24">
              <div className="col-end-24 col-span-22 col-start-2 mb-100px">
                <DynamicComponent {...nestedBlok} />
              </div>
            </div>
          );
        })
);
SeoContent.displayName = 'SeoContent';

/**
 * PCP PAGE COMPONENT
 */
const PCPPage: FC<React.PropsWithChildren<PCPPageProps>> = ({
  facets,
  routeFilter,
  story,
  initialVehicles,
  host,
}) => {
  const {
    make,
    price,
    bodyColor,
    equipment,
    drive,
    interior,
    fuelType,
    emission,
    vehicleType,
    vehicleConditions,
    merchantUuids,
  } = facets || {
    make: [],
    bodyColor: [],
    price: [],
    equipment: {},
    interior: {},
    fuelType: {},
    emission: {},
    drive: {},
    vehicleType: {},
    vehicleConditions: {},
    merchantUuids: {},
  };

  const dispatch = useDispatch();
  const router = useRouter();
  const whiteLabel = useWhiteLabel();
  const breakpoint = useScreenBreakpoint();
  const [visibleFilter, setVisibleFilter] = useState<boolean>(false);
  const [isPCPLoading, setIsPCPLoading] = useState<boolean>(false);
  const parsedUrlQuery = getParsedUrlQuery(router.query);
  const hasFavorites = featureflags.favoritesCompareEnabled;

  const intl = useIntl();

  // refs to suppress effects firing on initial render
  const didFiltersUpdate = useRef(false);
  // store original routeFilter object
  const routeFilterRef = useRef(routeFilter);
  // only update ref if routeFilter changes
  if (!isEqual(routeFilterRef.current, routeFilter)) {
    routeFilterRef.current = routeFilter;
  }

  const merchantLocations = {
    merchantLocation: merchantUuids,
  };

  const defaultPriceRange = useMemo(
    () => getDefaultPriceRange(facets.price),
    [facets.price]
  );
  // selector hook to get all pcp redux filter states
  const redux = usePCPReduxState(defaultPriceRange);

  const createSorting = (parsedUrlQuery: IParsedQuery) => {
    if (whiteLabel) {
      const { catalogDefaultSorting } = whiteLabel;
      const { subject, direction } = catalogDefaultSorting;

      return createVehiclesSortingObject({ subject, direction });
    } else {
      let sortingObject;
      if (typeof parsedUrlQuery?.sorting === 'string') {
        try {
          sortingObject = JSON.parse(parsedUrlQuery.sorting);
        } catch (e) {
          console.error('Failed to parse sorting JSON:', e);
          sortingObject = {};
        }
      } else {
        sortingObject = parsedUrlQuery?.sorting || {};
      }

      return createVehiclesSortingObject(sortingObject);
    }
  };

  const initialSorting = createSorting(parsedUrlQuery);

  const { loading, data, previousData } = useQuery(QueryVehicles, {
    variables: {
      filter: {
        ...routeFilter,
        ...createVehiclesFilterObject(parsedUrlQuery, false),
      },
      sorting: redux.sorting || initialSorting,
      pager: createVehiclesPagerObject({
        page: parsedUrlQuery.page,
        limit: parsedUrlQuery.itemsPerPage,
      }),
    },
    fetchPolicy: 'network-only',
  });
  const vehiclesData = data ?? previousData ?? initialVehicles;
  const aggregation: number = vehiclesData?.vehicles?.aggregation.count ?? 0;
  const vehicles = vehiclesData?.vehicles?.results || [];
  const [financeOverlayClick, setFinanceOverlayClick] =
    useState<boolean>(false);
  const [pagesCount, setPagesCount] = useState<number>(0);
  const [ROUTE_FILTER_KEY, ROUTE_FILTER_VALUE] =
    (routeFilter && Object.entries(routeFilter)[0]) || [];

  // persist facets to redux
  useEffect(
    () => {
      dispatch(setFacets(facets));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets]
  );

  // persist url page number, itemsPerPage and sorting to redux
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const { page, itemsPerPage, sorting } = getParsedUrlQuery(router.query);

    // Set sorting from query params or catalogDefaultSorting for WL
    if (!sorting && whiteLabel) {
      const { catalogDefaultSorting } = whiteLabel;
      const { subject, direction } = catalogDefaultSorting;

      dispatch(setSorting({ subject: subject, direction: direction }));
    }
    if (sorting && !isEqual(sorting, redux.sorting)) {
      dispatch(setSorting(sorting));
    }

    // Persist page and itemsPerPage to Redux
    if (page && page !== redux.pageNumber) {
      dispatch(setPageNumber(page));
    }
    if (itemsPerPage && itemsPerPage !== redux.itemsPerPage) {
      dispatch(setItemsPerPage(itemsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  // persist filters set by route path and query params to redux
  useEffect(
    () => {
      if (!router.isReady) {
        return;
      }
      const parsedUrlQuery = getParsedUrlQuery(router.query);
      const urlFilters = getUrlFilters(parsedUrlQuery);

      // allow route filter overrides
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const filter = getPCPFilter(
        {
          ...routeFilter,
          ...urlFilters,
        } as SaveFilterToRedux,
        price,
        whiteLabel?.merchantOrigin
      );

      dispatch(setAllFiltersAndSetFlag(filter));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.isReady, routeFilterRef.current]
  );

  // reset pagination on new pcp page
  useEffect(
    () => {
      if (!isEqual(routeFilter, redux.previousRouteFilter)) {
        dispatch(setPageNumber(1));
      }
      // store previous route filter on unmount
      return () => {
        dispatch(setPreviousRouteFilter(routeFilter));
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routeFilter]
  );

  useEffect(() => {
    const count = getPagesCount(aggregation, redux.itemsPerPage);
    setPagesCount(count);
  }, [aggregation, redux.itemsPerPage]);

  /*****************************************************************************
   * SAVE FILTERS TO URL QUERY
   ****************************************************************************/
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (!didFiltersUpdate.current) {
      didFiltersUpdate.current = true;
      return;
    }

    const newFiltersQuery = getQueryParams(redux, facets, routeFilter);

    // take existing query but without dealer & page params
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { dealer: _, page: __, ...existingFiltersQuery } = router.query;

    // first, we compare only filters (incl. sorting and per page, but without page param)
    // any filter change resets pagination, that's why page param is excluded
    if (!isEqual(existingFiltersQuery, newFiltersQuery)) {
      router.replace({
        pathname: router.asPath.split('?')[0],
        query: newFiltersQuery,
      });
    } else {
      // if they are the same, we check the page param next
      // and if it's different we add it to the current query
      const existingFullQuery = { ...router.query };

      if (!isNaN(redux.pageNumber)) {
        if (redux.pageNumber > 1 && redux.pageNumber <= pagesCount) {
          existingFullQuery.page = `${redux.pageNumber}`;
        } else if (redux.pageNumber === 1) {
          delete existingFullQuery.page;
        }

        if (
          aggregation &&
          redux.pageNumber > Math.ceil(aggregation / redux.itemsPerPage)
        ) {
          existingFullQuery.page = `${pagesCount}`;

          dispatch(setPageNumber(pagesCount));
        }
      }

      if (!isEqual(router.query.page, existingFullQuery.page)) {
        router.replace({
          pathname: router.asPath.split('?')[0],
          query: existingFullQuery,
        });
      }
    }

    // deps should use primitive types to compare, otherwise useEffect will run multiple times,
    // cos a reference type has always a new reference on every render,
    // to solve this we use JSON.stringify for reference types

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    // primitive types
    router.isReady,
    redux.itemsPerPage,
    aggregation,
    redux.pageNumber,
    pagesCount,
    // reference types
    JSON.stringify(defaultPriceRange),
    JSON.stringify(redux.filterBodyColors),
    JSON.stringify(redux.filterLocation),
    JSON.stringify(redux.filterMerchantLocation),
    JSON.stringify(redux.filterMakeModel),
    JSON.stringify(redux.filterDrive),
    JSON.stringify(redux.filterPriceRange),
    JSON.stringify(redux.filterAccordionOpenStates),
    JSON.stringify(redux.filterInterior),
    JSON.stringify(redux.filterFuel),
    JSON.stringify(redux.filterVehicleType),
    JSON.stringify(redux.filterVehicleCondition),
    JSON.stringify(redux.filterEquipment),
    JSON.stringify(redux.filterEmission),
    JSON.stringify(redux.sorting),
    pagesCount,
    /* eslint-enable react-hooks/exhaustive-deps */
  ]);

  const hasNoResults = useMemo(
    () => !loading && vehicles.length === 0 && router.isReady,
    [vehicles.length, loading, router.isReady]
  );

  const handlePageClick = useCallback(
    (n: number) => {
      dispatch(setPageNumber(n));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [dispatch]
  );

  const gotoPDP = (id: string, model: string, mark: string) => {
    dispatch(closeOverlay());
    setFinanceOverlayClick(true);

    const pdpLink = createLinkForRouting(
      FinanceTypeEn.FINANCING,
      Routes.PDP_VEHICLES,
      {
        markName: mark && `/${mark}`,
        modelName: model && `/${model}`,
        id: id && `/${id}`,
      }
    );
    if (pdpLink) {
      router.push(pdpLink).then(() => {
        setFinanceOverlayClick(false);
      });
    }
  };

  const openOverlayFinance = useCallback(
    (
      summaryText: string,
      legalText: string,
      financeType: SchemaTypes.PaymentType,
      vehicleName: string,
      vehicleId: string,
      vehicleModel: string,
      vehicleMark: string
    ) => {
      dispatch(
        openOverlay({
          title: translate(intl, `ui.PDP.FOOTER.FINANCE_TYPE.${financeType}`),
          children: (
            <FinanceDetailsOverlay
              legalText={legalText}
              summaryText={summaryText}
              closeOverlay={() => gotoPDP(vehicleId, vehicleModel, vehicleMark)}
              vehicleName={vehicleName}
              financeTypeText={translate(
                intl,
                `ui.FINANCE_DETAILS.${financeType}`
              )}
              buttonText={translate(
                intl,
                `ui.PDP.FOOTER.FINANCE_FOOTER_BUTTON`
              )}
            />
          ),
        })
      );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const handleChangeViewMode = useCallback(
    (viewMode: ViewModes) => {
      dispatch(setViewMode(viewMode));
    },
    [dispatch]
  );

  const handleSortingChange = useCallback(
    ({ subject, direction }: any) => {
      dispatch(setSorting({ subject, direction }));
      if (redux.pageNumber !== 1) dispatch(setPageNumber(1));
    },
    [dispatch, redux.pageNumber]
  );

  const handleItemsPerPageChange = useCallback(
    ({ value }: any) =>
      dispatch(setItemsPerPage(parseInt(value as string, 10))),
    [dispatch]
  );

  const handleOpenOverlay = useCallback(
    (
      summaryText: any,
      legalText: any,
      financeType: any,
      vehicleName: any,
      vehicleId: any,
      vehicleModel: any,
      vehicleMark: any
    ) =>
      openOverlayFinance(
        summaryText,
        legalText,
        financeType,
        vehicleName,
        vehicleId,
        vehicleModel,
        vehicleMark
      ),
    [openOverlayFinance]
  );

  const setIsLoadingOverlay = (isOpen: boolean) => {
    setIsPCPLoading(isOpen);
  };

  const makeModelFilterProps = useMemo(
    () => ({
      makeItems: make?.map((item: any) => {
        return {
          value: item.name,
          text: item.displayName,
        };
      }),
    }),
    [make]
  );

  const locationFilterProps = useMemo(
    () => ({
      location: redux.filterLocation?.location as Option,
      radius: redux.filterLocation?.radius,
    }),
    [redux.filterLocation?.location, redux.filterLocation?.radius]
  );

  const colorListFilterProps = useMemo(
    () => ({
      colors: bodyColor?.map((item: any, index: number) => {
        return {
          displayName: item.displayName,
          name: item.name,
          hex: item.hex,
          isDefaultActive:
            redux.filterBodyColors &&
            redux.filterBodyColors.indexOf(item.name) !== -1,
          highlighted:
            index < consts.PCP.FILTERS.BODY_COLOR_MAX_INITIAL_VISIBLE_ITEMS,
        };
      }),
    }),
    [bodyColor, redux.filterBodyColors]
  );

  const priceFilterProps = useMemo(
    () => ({
      priceRanges: price,
    }),
    [price]
  );

  const vehicleConditionFilterProps = useMemo(
    () => ({
      conditions: vehicleConditions,
    }),
    [vehicleConditions]
  );

  const interiorFilterProps = useMemo(
    () => ({
      types: (interior.interiorType as SchemaTypes.InteriorType[]) ?? [],
      colors:
        ((interior.interiorColor as SchemaTypes.InteriorColor[])?.map(
          (colorItem, i) => {
            return {
              name: colorItem?.name,
              displayName: colorItem?.displayName,
              hex: colorItem?.hex,
              highlighted:
                i < consts.PCP.FILTERS.INTERIOR_COLOR_MAX_INITIAL_VISIBLE_ITEMS,
            };
          }
        ) as SchemaTypes.InteriorColor[]) ?? [],
    }),
    [interior.interiorColor, interior.interiorType]
  );

  const equipmentFilterProps = useMemo(
    () => ({
      equipments: {
        aircon: equipment.aircon ?? [],
        comfort: equipment.comfort ?? [],
        security: equipment.security ?? [],
        entertainment: equipment.entertainment ?? [],
      },
    }),
    [
      equipment.aircon,
      equipment.comfort,
      equipment.entertainment,
      equipment.security,
    ]
  );

  const merchantLocationFilterProps = useMemo(
    () => ({
      merchantLocations: {
        merchantLocation: merchantLocations.merchantLocation ?? [],
      },
    }),
    [merchantLocations.merchantLocation]
  );

  const mobileFilterParam = useMemo(
    () => ({
      setFilterVisible: setVisibleFilter,
    }),
    []
  );

  const today = new Date().toJSON().slice(0, 10);
  const imageBase =
    process.env.IMAGE_BASE_URL || process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '';

  const productsJsonLd: Thing = {
    '@type': 'ItemList',
    url: host + router.asPath,
    numberOfItems: vehicles?.length,
    itemListElement: vehicles?.map(
      (vehicle: SchemaTypes.Vehicle, index: number) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: vehicle.title,
          image: vehicle?.imageUrl ? `${imageBase}/${vehicle.imageUrl}` : '',
          description: vehicle.trim,
          offers: {
            '@type': 'Offer',
            availability: 'InStock',
            priceCurrency: 'EUR',
            priceValidUntil: today,
            price: vehicle.price || 0,
            url:
              host +
              createLinkForRouting(FinanceTypeEn.EMPTY, Routes.PDP_VEHICLES, {
                markName: vehicle.makeName && `/${vehicle.makeName}`,
                modelName: vehicle.modelName && `/${vehicle.modelName}`,
                id: vehicle.id && `/${vehicle.id}`,
              }),
          },
          brand: {
            '@type': 'Brand',
            name: vehicle.makeName || '',
          },
          sku: vehicle.id,
          mpn: vehicle.id,
        },
      })
    ),
  };

  const footnotes = [
    {
      content: (
        <Markdown {...markdownProps}>
          {translate(intl, 'ui.PDP.ENVIRONMENT.CO2_EFFICIENCY_ADDITIONAL_INFO')}
        </Markdown>
      ),
      type: 'emission',
    },
  ];

  return (
    <PCPLayout
      suppressHydrationWarning
      seoContent={<SeoContent story={story} whiteLabel={whiteLabel} />}
      headerContent={
        <HeaderContainer
          numOfNotificationsFavorite={
            consts.NAVIGATION_DUMMY_NOTIFICATIONS_NUMBER
          }
        />
      }
      horizontalToolbar={
        <HorizontalToolbar
          onViewTypeChange={handleChangeViewMode}
          resultsNumber={aggregation || 0}
          viewMode={redux.viewMode}
          onItemsPerPageChange={handleItemsPerPageChange}
          sorting={redux.sorting}
          onSortingChange={handleSortingChange}
          itemsPerPage={redux.itemsPerPage}
          disabled={hasNoResults}
          whiteLabelDefaultSorting={whiteLabel?.catalogDefaultSorting}
        />
      }
      sidebarContent={
        <Sidebar
          locationFilterProps={locationFilterProps}
          makeModelFilterProps={makeModelFilterProps}
          colorListFilterProps={colorListFilterProps}
          priceFilterProps={priceFilterProps}
          equipmentFilterProps={equipmentFilterProps}
          merchantLocationFilterProps={merchantLocationFilterProps}
          driveFilterProps={drive}
          vehicleConditionFilterProps={vehicleConditionFilterProps}
          interiorFilterProps={interiorFilterProps}
          fuelFilterProps={fuelType}
          emissionFilterProps={emission}
          vehicleTypeFilterProps={vehicleType}
          merchantUuids={merchantUuids}
        />
      }
      footerContent={<Footer />}
      mobileFilter={<MobileFilter param={mobileFilterParam} />}
      isVisible={visibleFilter}
      isLoading={loading || financeOverlayClick}
      isPCPLoading={isPCPLoading}
    >
      {story?.meta_data && <SeoContainer {...story.meta_data} />}
      <JsonLd value={productsJsonLd} />
      <FilterTagsContainer routeFilter={routeFilter} facets={facets} />
      <div className="mb-100px mt-40px">
        {Array.isArray(vehicles) && vehicles.length > 0 && (
          <ProductCardList
            vehicles={vehicles}
            view={
              breakpoint > SCREEN_BREAKPOINT.MD ? redux.viewMode : undefined
            }
            selectedFinance={redux.filterPriceRange}
            openOverlay={handleOpenOverlay}
            openLoadingOverlay={setIsLoadingOverlay}
            filterCategory={ROUTE_FILTER_KEY}
            filterValue={ROUTE_FILTER_VALUE}
            setPersistVehicleState={setPersistVehicleState}
            isWhiteLabel={whiteLabel !== undefined}
            hasFavorites={hasFavorites}
          />
        )}
        {hasNoResults && (
          <NoProductCardsResults
            headline={translate(intl, 'ui.NO_RESULTS.PRODUCT_CARDS.MESSAGE')}
          />
        )}
      </div>
      {pagesCount > 0 && !hasNoResults && (
        <Pagination
          pagesCount={pagesCount}
          activePage={redux.pageNumber}
          onPageClick={handlePageClick}
          pagesVisibleCountLg={8}
        />
      )}
      {!hasNoResults && <Footnote items={footnotes} showIdentifier={false} />}
    </PCPLayout>
  );
};

export const getServerSideProps: GetServerSideProps<PCPPageProps> = async (
  context
) => {
  const { params, query } = context;
  const slug = params && Array.isArray(params.slug) ? params.slug : [];
  const route = [Routes_EN.CARS.ALL, ...slug].join('/');
  const integrationSlug = params?.dealer as string;
  const whiteLabelConfig = await getWhiteLabelConfig(integrationSlug);
  const apolloClient = initializeApollo(
    undefined,
    whiteLabelConfig?.merchantUuids,
    whiteLabelConfig?.merchantOrigin
  );

  // Fetch facets

  const {
    data: { facets },
  } = await apolloClient.query({
    query: QueryFacets,
    variables: {
      origin: whiteLabelConfig?.merchantOrigin,
    },
    errorPolicy: 'all',
  });

  // Fetch route data

  let routeFilter: RouteFilter;
  let story: any;

  if (slug.length > 2) {
    // routes with more than 2 parameters are not supported by this page
    return {
      notFound: true,
    };
  } else if (routes[route]) {
    routeFilter = routes[route].routeFilter || {};
    story = routes[route].story;
  } else {
    const [make, model] = slug;

    if (make && facets.make.find((makeFacet: any) => makeFacet.name === make)) {
      // set requested make as filter
      routeFilter = {
        brands: {
          makeName: make,
        },
      };
    } else {
      // requested make not found
      return {
        notFound: true,
      };
    }

    if (model) {
      // fetch models of selected brand
      const {
        data: { models },
      } = await apolloClient.query<ModelsQuery>({
        query: QueryModelsByMake,
        variables: { makeName: make },
        errorPolicy: 'all',
      });

      if (models.find((modelFacet: any) => modelFacet.name === model)) {
        // set requested make and model as filter
        routeFilter = {
          brands: {
            makeName: make,
            modelName: model,
          },
        };
      } else {
        // requested model not found
        return {
          notFound: true,
        };
      }
    }

    story = null;
  }

  const filter = getPCPFilter(
    {
      ...routeFilter,
      ...getFilterValues(context.resolvedUrl),
    } as DefaultFilter,
    facets.price,
    whiteLabelConfig?.merchantOrigin
  );

  let initialSorting;
  if (whiteLabelConfig) {
    const { catalogDefaultSorting } = whiteLabelConfig;
    const { subject, direction } = catalogDefaultSorting;

    initialSorting = createVehiclesSortingObject({ subject, direction });
  } else {
    initialSorting = createVehiclesSortingObject(
      query?.sorting ? JSON.parse(String(query?.sorting)) : {}
    );
  }

  const { data: initialVehicles } = await apolloClient.query({
    query: QueryVehicles,
    variables: {
      filter,
      sorting: initialSorting,
      pager: createVehiclesPagerObject({
        page: query?.page ? Number(query.page) : 1,
        limit: query?.itemsPerPage ? Number(query.itemsPerPage) : 24,
      }),
    },
  });

  const host =
    getHostFromWhitelabelConfig(whiteLabelConfig) || 'https://alles.auto';

  const props: PCPPageProps = {
    facets,
    routeFilter,
    story,
    initialVehicles,
    host,
  };

  return {
    props,
  };
};

export default memo(PCPPage);
